import { ExclamationCircleOutlined } from "@ant-design/icons";
import { SalePlan, SalePlanOption } from "@api/saleApi";
import PopupBackground from "@component/PopupBackground";
import { fetchCreateChangePrice, fetchUpdateChangePrice, SaleState } from "@redux/saleSlice";
import { debounceAsync } from "@utils/common";
import MathRound from "@utils/MathRound";
import { Button, DatePicker, Input, InputNumber, Modal, Radio, Row, Select, Space, Typography } from "antd";
import locale from "antd/es/date-picker/locale/zh_TW";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import fontStyle from "src/styles/fontStyle";
import styled from "styled-components";
import priceUtils, { getExpGrossMarginPayload, RadioType } from "./utils/utils";

const Wrapper = styled.div`
  position: relative;
  max-width: 70vw;
  padding: 20px;
  background: ${({ theme }) => theme.colorNeutral100};
  max-height: 80vh;
  overflow: auto;
`;

const CustomButton = styled(Button)`
  margin-right: 8px;
`;
const CustomInputNumber = styled(InputNumber)<{ isDefaultVIPPrice?: boolean }>`
  color: ${({ isDefaultVIPPrice }) => (isDefaultVIPPrice ? "grey" : "black")};
`;

const CustomRow = styled(Row)`
  margin-bottom: 12px;
`;

const CustomRadio = styled(Radio)<{ marginBottom?: string }>`
  display: block;
  margin-bottom: ${({ marginBottom }) => marginBottom};
`;

const CustomRadioGroup = styled(Radio.Group)`
  padding-top: 4px;
`;

const CustomSelect = styled(Select)`
  width: 100%;
`;

const CustomInput = styled(Input)<{ width?: string; gap?: boolean }>`
  width: ${({ width }) => width || "52px"};
  margin: ${({ gap }) => gap && "0px 10px"};
`;

const Text = styled.span`
  margin-right: 30px;
  min-width: 100px;
  padding: 8px 0;
  ${fontStyle("14px", "16px")};
`;

const Error = styled.span`
  color: #ec6922;
  position: absolute;
  left: 20px;
  bottom: 20px;
  ${fontStyle("14px", "16px")};
`;

type Props = {
  isLast: boolean;
  close: () => void;
  onNext: () => void;
  salePlanListResult: SalePlan[];
  getSalePlanList: () => void;
};

export default function ChangePriceModal(props: Props) {
  const { isLast, close, onNext, salePlanListResult, getSalePlanList } = props;

  const dispatch = useDispatch();
  const {
    salePageDetail,
    needReload,
    changePriceError,
    currentPlanChangePrice,
    optionListResult,
    currentSalePlan,
  } = useSelector(SaleState);

  const [salePlanMap, setSalePlanMap] = useState<Map<number, SalePlan>>(new Map());
  const [salePlan, setSalePlan] = useState<SalePlan>();
  const [optionDiscountPeriod, setOptionDiscountPeriod] = useState<moment.Moment[]>([]);
  const [radio, setRadio] = useState<RadioType>(RadioType.DEFAULT);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [pageTypeAnyField, setPageTypeAnyField] = useState<{
    specialPrice?: number;
    commissionRate?: number;
    vipPrice?: number;
  }>({
    specialPrice: undefined,
    commissionRate: undefined,
    vipPrice: undefined,
  });
  const [updateOptions, setUpdateOptions] = useState<SalePlanOption[]>([]);
  const [VIPGrossMargin, setVIPGrossMargin] = useState<number>();

  const activeSalePlanListResult = useMemo(
    () => salePlanListResult.filter((item) => item.isActive),
    [salePlanListResult],
  );

  const computedPriceDiscount = useMemo(() => {
    const specialPrice = pageTypeAnyField?.specialPrice;
    if (
      salePlan &&
      typeof specialPrice === "number" &&
      typeof salePlan.price === "number" &&
      typeof salePlan.optionsCount === "number" &&
      salePlan.optionsCount !== 0
    ) {
      return Math.floor(((salePlan.price - specialPrice) / salePlan.optionsCount) * 10000) / 10000;
    }
    return "N/A";
  }, [salePlan, pageTypeAnyField]);

  const handleOnVIPPriceChange = (value: any) => {
    if (typeof value === "number" || value === null) {
      handleOnFieldChange("vipPrice", value);
    }
  };

  const handleOnFieldChange = (fieldName: string, fieldValue: any) => {
    let obj = {};
    switch (fieldName) {
      case "specialPrice":
        obj = { specialPrice: parseInt(fieldValue, 10), vipPrice: undefined };
        break;
      case "commissionRate":
        obj = { commissionRate: parseFloat(fieldValue) };
        break;
      case "vipPrice":
        obj = { vipPrice: fieldValue };
        break;
      default:
        break;
    }
    setPageTypeAnyField((prev) => ({ ...prev, ...obj }));
  };

  const onSubmitCheck = () => {
    // 黑卡試算毛利率<最低限額時，跳警告
    if (VIPGrossMargin && VIPGrossMargin < 0) {
      Modal.confirm({
        title: <Typography.Text type="danger">警告：</Typography.Text>,
        icon: <ExclamationCircleOutlined />,
        content: (
          <Space direction="vertical">
            <Typography.Text type="danger">您目前設定的黑卡價將導致負毛利率（虧損）。</Typography.Text>
            <Typography.Text type="danger">請確認是否繼續操作。</Typography.Text>
          </Space>
        ),
        onOk: () => onSubmit(),
        okText: "確認",
        cancelText: "取消",
      });
      return;
    }
    onSubmit();
  };

  const onSubmit = () => {
    if (!salePlan) return;
    setErrorMessage("");
    const validateData = {
      price: salePlan.price,
      specialPrice: pageTypeAnyField.specialPrice,
      periodType: radio,
      startAt: optionDiscountPeriod[0]?.format("YYYY-MM-DD HH:mm:ss"),
      endAt: optionDiscountPeriod[1]?.format("YYYY-MM-DD HH:mm:ss"),
      vipPrice: pageTypeAnyField.vipPrice,
    };

    const { status, message: errMsg } = priceUtils.changePriceValidator.planField(validateData);
    if (!status) {
      setErrorMessage(errMsg);
      return;
    }

    const commissionRateDiscount = pageTypeAnyField.commissionRate
      ? MathRound(pageTypeAnyField.commissionRate || 0, 1)
      : undefined;

    if (currentPlanChangePrice && updateOptions.length === 0) {
      dispatch(
        fetchUpdateChangePrice({
          salesPlan: salePlan.id,
          startAt: validateData.startAt,
          endAt: validateData.endAt,
          specialPrice: validateData.specialPrice,
          commissionRateDiscount,
          optionsDiscountsIds: currentPlanChangePrice.optionsInfo.map((item) => item.optionsDiscountsId),
          optionsDiscounts: [],
          vipPrice: validateData.vipPrice,
          planDiscountId: currentPlanChangePrice.planDiscountId,
        }),
      );
    } else {
      dispatch(
        fetchCreateChangePrice({
          salesPlan: salePlan.id,
          startAt: validateData.startAt,
          endAt: validateData.endAt,
          specialPrice: validateData.specialPrice,
          commissionRateDiscount,
          optionsDiscounts: [],
          vipPrice: validateData.vipPrice,
        }),
      );
    }
  };

  // 重新刷新方案列表，因為有可能新增方案
  useEffect(() => {
    getSalePlanList();
  }, [getSalePlanList]);

  // 編輯初始化
  useEffect(() => {
    if (currentPlanChangePrice) {
      const { id, startAt, endAt, price, optionsInfo, vipPrice } = currentPlanChangePrice;
      if (startAt) {
        setRadio(RadioType.TIMING);
        setOptionDiscountPeriod([moment(startAt), moment(endAt)]);
      } else {
        setRadio(RadioType.DEFAULT);
      }

      const firstRate = optionsInfo.find((item) => item.commissionRateDiscount > 0);
      const updateSalePlan = salePlanMap.get(id)!;
      if (updateSalePlan) {
        setSalePlan(updateSalePlan);
        const optionCount = price / optionsInfo[0].optionUnitPrice;

        const specialPrice =
          optionsInfo[0].optionUnitPrice * optionCount -
          Number((optionsInfo[0].priceDiscount * optionCount).toFixed(0));

        setPageTypeAnyField({
          specialPrice: specialPrice ?? 0,
          commissionRate: firstRate ? firstRate.commissionRateDiscount : 0,
          vipPrice,
        });
      }
    }
  }, [currentPlanChangePrice, salePlanMap]);

  useEffect(() => {
    if (!currentPlanChangePrice) {
      const validCurrentSalePlan = currentSalePlan?.isActive ? currentSalePlan : undefined;
      const selectedSalePlan = salePlan || activeSalePlanListResult[0];
      setSalePlan(validCurrentSalePlan || selectedSalePlan);
    }

    salePlanListResult.forEach((item) => {
      setSalePlanMap((prev) => new Map(prev.set(item.id, item)));
    });
  }, [salePlanListResult, activeSalePlanListResult, currentPlanChangePrice, currentSalePlan, salePlan]);

  useEffect(() => {
    if (salePlan) {
      setErrorMessage("");

      if (changePriceError) {
        setErrorMessage(changePriceError);
      }
    }
  }, [salePlan, changePriceError, salePageDetail, pageTypeAnyField]);

  useEffect(() => {
    if (needReload) {
      onNext();
    }
  }, [needReload, onNext]);

  useEffect(() => {
    // 多繞這一層的原因是
    // 如果新增變價過後又想增加 plan option，照上面的邏輯會進到 update，但是 update 沒有 optionsDiscountsId
    // 這樣會有問題，所以新增 plan option 就算在 create 那邊
    if (salePlan && currentPlanChangePrice) {
      const set = new Set();
      currentPlanChangePrice.optionsInfo.forEach((item) => {
        set.add(item.optionId);
      });
      salePlan.options.forEach((item) => {
        if (!set.has(item.id)) {
          setUpdateOptions((prev) => [...prev, item]);
        }
      });
    }
  }, [salePlan, currentPlanChangePrice]);

  const debounceExpMargin = useMemo(
    () =>
      debounceAsync(async (payload: getExpGrossMarginPayload) => {
        const expMargin = await priceUtils.getExpGrossMargin(payload);
        if (expMargin) {
          setVIPGrossMargin(expMargin);
        }
      }, 500),
    [],
  );

  // 帶入試算毛利率
  useEffect(() => {
    if (!pageTypeAnyField.vipPrice) {
      debounceExpMargin.cancel();
      setVIPGrossMargin(undefined); // 清除毛利率
      return;
    }
    if (salePlan && pageTypeAnyField.vipPrice) {
      const startAt = optionDiscountPeriod[0]?.format("YYYY-MM-DD");
      const newPriceD = { "": pageTypeAnyField.specialPrice || salePlan.price };

      debounceExpMargin({
        planId: salePlan.id,
        newPriceD,
        expVipPrice: pageTypeAnyField.vipPrice,
        startAt,
      });
    }
  }, [debounceExpMargin, optionDiscountPeriod, pageTypeAnyField.specialPrice, pageTypeAnyField.vipPrice, salePlan]);

  return (
    <PopupBackground close={close} fixed>
      <Wrapper>
        <CustomRow align="middle" wrap={false}>
          <Text>方案名稱</Text>
          {currentPlanChangePrice ? (
            <Text>{salePlan?.name}</Text>
          ) : (
            <CustomSelect
              value={salePlan?.id}
              onChange={(id) => {
                setSalePlan(salePlanMap.get(parseInt(id as string, 10))!);
                setPageTypeAnyField({
                  specialPrice: undefined,
                  commissionRate: undefined,
                  vipPrice: undefined,
                });
                setVIPGrossMargin(undefined);
              }}
            >
              {activeSalePlanListResult.map((item) => (
                <Select.Option key={item.name} value={item.id}>
                  {item.name}
                </Select.Option>
              ))}
            </CustomSelect>
          )}
        </CustomRow>
        <CustomRow>
          <Text>方案售價</Text>
          <Text>{`$${salePlan?.price}`}</Text>
        </CustomRow>
        <CustomRow>
          <Text>方案優惠價</Text>
          <CustomInputNumber
            width="70px"
            value={pageTypeAnyField.specialPrice}
            onChange={(value) => {
              handleOnFieldChange("specialPrice", value);
              handleOnFieldChange("vipPrice", undefined);
              setVIPGrossMargin(undefined);
            }}
            placeholder={`${salePlan?.price}`}
          />
        </CustomRow>
        <CustomRow>
          <Text>黑卡會員價</Text>
          <Space>
            <CustomInputNumber
              min={0}
              onChange={(value) => handleOnVIPPriceChange(value)}
              value={pageTypeAnyField.vipPrice}
              isDefaultVIPPrice={!pageTypeAnyField.vipPrice}
              placeholder={`${priceUtils.getDefaultVIPprice(pageTypeAnyField.specialPrice || salePlan?.price || 0)}`}
            />
            <Text style={{ color: VIPGrossMargin && VIPGrossMargin <= 0 ? "red" : "#40a9ff" }}>{`(試算毛利率：${
              VIPGrossMargin ?? "-"
            }%)`}</Text>
          </Space>
        </CustomRow>
        <CustomRow align="middle" wrap={false}>
          <Text>每入折扣金額</Text>
          {computedPriceDiscount}
        </CustomRow>
        <CustomRow align="middle" wrap={false}>
          <Text>方案抽成</Text>
          {/* 0831 抽成％數預設從 option 來，所有 option 會有同個 commissionRate */}
          <span>{`${optionListResult.results[0].vpcInfo.commissionRate}% -`}</span>
          <CustomInput
            width="70px"
            gap
            type="number"
            value={pageTypeAnyField.commissionRate}
            onChange={(e) => handleOnFieldChange("commissionRate", e.target.value)}
          />
          <span>%</span>
        </CustomRow>
        <CustomRow>
          <Text>優惠時間</Text>
          <CustomRadioGroup value={radio} onChange={(e) => setRadio(e.target.value)}>
            <CustomRadio marginBottom="10px" value={RadioType.DEFAULT}>
              預設
            </CustomRadio>
            <CustomRadio value={RadioType.TIMING}>
              <DatePicker.RangePicker
                locale={locale}
                format="YYYY-MM-DD HH:mm:ss"
                showTime={{
                  hideDisabledOptions: true,
                  defaultValue: [moment("00:00:00", "HH:mm:ss"), moment("23:59:59", "HH:mm:ss")],
                }}
                value={optionDiscountPeriod as any}
                onFocus={() => setRadio(RadioType.TIMING)}
                onChange={(val) => setOptionDiscountPeriod(val as moment.Moment[])}
              />
            </CustomRadio>
          </CustomRadioGroup>
        </CustomRow>
        <Row justify="end">
          <CustomButton onClick={close}>取消</CustomButton>
          <Button type="primary" onClick={onSubmitCheck}>
            {isLast ? "確認" : "下一步"}
          </Button>
        </Row>
        <Error>{errorMessage}</Error>
      </Wrapper>
    </PopupBackground>
  );
}
